/**
 * Өртөг хуваарилалт — `/purchasing/:id`-ийн нэг хэсэг (CLAUDE.md №4).
 *
 * Урсгал: зардал хадгалах (`POST /costs`) → урьдчилсан тооцоо
 * (`GET /cost-preview`) → хуваарилах (`POST /allocate`, НЭГ Л УДАА).
 *
 * ⚠️ «Тооцоо харах» нь ЭХЛЭЭД зардлыг хадгална. Урьдчилсан тооцоо нь
 *    DB-д хадгалсан утгаас бодогддог — хадгалаагүй бол маягтын тоог
 *    харж байна гэж бодоод хуучин дүнгээр хуваарилна.
 *
 * ⚠️ Хуваарилалт ЗААВАЛ БИШ — хуваарилаагүй аялалын гутал ч зарагдана
 *    (шийдвэр 2026-09-16). Харин хуваарилсан аялалд гутал нэмэгдэхгүй
 *    (`PURCHASE_CLOSED`).
 *
 * ⚠️ 2026-09-16-наас хойш бүртгэгдсэн гутал ₮ өртөггүй (систем ₽-ийг ₮
 *    болгож бодохгүй). Тийм гутал байвал «Үнийн хувиар» хуваарилах
 *    боломжгүй — сервер 422 буцаана, «Ширхэгт тэнцүү» л ажиллана.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, formatMnt, formatQty, formatSerialDisplay, moneyInput } from '@boots/domain';
import { Section } from './section';
import { Button, ErrorNote, Field, Input, Modal, Select, Spinner, Table, Tabs, Td, Th } from './ui';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ALLOCATION_LABEL, type CostPreview, type PurchaseDetail } from '../lib/types';

type Method = PurchaseDetail['allocation'];

const METHODS = Object.keys(ALLOCATION_LABEL) as Method[];

const COST_FIELDS = [
  { key: 'transportMnt', label: 'Тээвэр' },
  { key: 'customsMnt', label: 'Гааль' },
  { key: 'otherMnt', label: 'Бусад' },
] as const;
type CostKey = (typeof COST_FIELDS)[number]['key'];

/** Хоосон = 0. Сөрөг, үсэг, 2-оос олон аравтын орон — буруу. */
const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const isZero = (v: string) => /^0+(\.0+)?$/.test(v);
/** `"1200000.00"` → `"1200000"` — маягтад илүү тэг харуулахгүй. */
const toField = (v: string) => (isZero(v) ? '' : v.replace(/\.0+$/, ''));

export function PurchaseCosting({ p }: { p: PurchaseDetail }) {
  const { hasPerm } = useAuth();

  if (p.costedAt) {
    return (
      <Section
        title="Өртөг хуваарилалт"
        subtitle={p.costedBy?.fullName}
        note={`хуваарилсан ${formatDateTime(p.costedAt)}`}
      >
        <CostFacts p={p} />
        <p className="text-meta text-ink-2">
          {ALLOCATION_LABEL[p.allocation]} · барааны өртөг {formatMnt(p.totals.goodsCostMnt)} + нэмэлт{' '}
          {formatMnt(p.totals.overheadMnt)} ={' '}
          <span className="num text-ink">{formatMnt(p.totals.landedCostMnt)}</span>
        </p>
      </Section>
    );
  }

  return (
    <Section title="Өртөг хуваарилалт" note="хуваарилагдаагүй">
      {hasPerm('purchasing.edit') ? <CostForm p={p} /> : <CostFacts p={p} />}
    </Section>
  );
}

function CostFacts({ p }: { p: PurchaseDetail }) {
  return (
    <dl className="flex flex-wrap gap-x-5 gap-y-1 text-meta">
      {[...COST_FIELDS, { key: 'overheadTotalMnt', label: 'Нийт зардал' } as const].map((f) => (
        <div key={f.key} className="flex items-baseline gap-1.5">
          <dt className="text-ink-2">{f.label}</dt>
          <dd className="num">{formatMnt(p[f.key])}</dd>
        </div>
      ))}
    </dl>
  );
}

function CostForm({ p }: { p: PurchaseDetail }) {
  const qc = useQueryClient();
  const [costs, setCosts] = useState<Record<CostKey, string>>({
    transportMnt: toField(p.transportMnt),
    customsMnt: toField(p.customsMnt),
    otherMnt: toField(p.otherMnt),
  });
  const [method, setMethod] = useState<Method>(p.allocation);
  const [previewing, setPreviewing] = useState(false);
  const invalid = COST_FIELDS.some((f) => costs[f.key].trim() !== '' && !MONEY_RE.test(costs[f.key].trim()));

  const save = useMutation({
    mutationFn: () =>
      api(`/purchases/${p.id}/costs`, {
        method: 'POST',
        body: {
          transportMnt: moneyInput(costs.transportMnt),
          customsMnt: moneyInput(costs.customsMnt),
          otherMnt: moneyInput(costs.otherMnt),
        },
      }),
    onSuccess: async () => {
      // Өмнө нээсэн тооцоо хуучин зардлаар кэшлэгдсэн — харуулахгүй
      qc.removeQueries({ queryKey: ['cost-preview', p.id] });
      await qc.invalidateQueries({ queryKey: ['purchase', p.id] });
      setPreviewing(true);
    },
  });

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-4">
        {COST_FIELDS.map((f) => (
          <Field key={f.key} label={`${f.label} (₮)`}>
            <Input
              value={costs[f.key]}
              onChange={(e) => setCosts((c) => ({ ...c, [f.key]: e.target.value }))}
              inputMode="decimal"
              placeholder="0"
              className="num text-right"
            />
          </Field>
        ))}
        <Field label="Хуваарилах арга">
          <Select
            value={method}
            onChange={(v) => setMethod(v as Method)}
            options={METHODS.map((m) => ({ value: m, label: ALLOCATION_LABEL[m] }))}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={invalid || save.isPending}>
          {save.isPending ? 'Хадгалж байна…' : 'Хадгалаад тооцоо харах'}
        </Button>
        <span className="text-label text-ink-3">
          {ALLOCATION_LABEL.BY_VALUE} — үнэтэй гутал их зардал үүрнэ · {ALLOCATION_LABEL.BY_UNIT} — ширхэг бүрд ижил
        </span>
      </div>

      {invalid ? <ErrorNote>Дүнг «1200000» эсвэл «1200000.50» хэлбэрээр оруулна уу</ErrorNote> : null}
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}

      {previewing ? (
        <CostPreviewModal p={p} method={method} onMethod={setMethod} onClose={() => setPreviewing(false)} />
      ) : null}
    </>
  );
}

/** Буцаах боломжгүй алхмын өмнөх анхааруулга. */
function costWarnings(p: PurchaseDetail, d: CostPreview | undefined): string[] {
  const out: string[] = [];
  if (!p.close.closedAt) {
    out.push(
      'Цуглуулагч аялалаа дуусгаагүй. Хуваарилсны дараа энэ аялалд гутал нэмэгдэхгүй — утсанд үлдсэн бүртгэл татгалзагдана.',
    );
  }
  const { expected, registered } = p.reconciliation;
  if (expected > registered) {
    out.push(`${formatQty(expected - registered)} гутал дутуу бүртгэгдсэн (ирэх ёстой ${formatQty(expected)}).`);
  }
  if (d && isZero(d.overheadTotalMnt)) {
    out.push('Нэмэлт зардал 0₮ — гутлын өртөг зөвхөн барааны үнээр түгжигдэнэ.');
  }
  return out;
}

function CostPreviewModal({
  p, method, onMethod, onClose,
}: {
  p: PurchaseDetail;
  method: Method;
  onMethod: (m: Method) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: ['cost-preview', p.id, method],
    queryFn: () => api<Envelope<CostPreview>>(`/purchases/${p.id}/cost-preview?method=${method}`),
  });

  const allocate = useMutation({
    mutationFn: () => api(`/purchases/${p.id}/allocate`, { method: 'POST', body: { method } }),
    onSuccess: async () => {
      onClose();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['purchase', p.id] }),
        qc.invalidateQueries({ queryKey: ['purchase-items', p.id] }),
        qc.invalidateQueries({ queryKey: ['purchases'] }),
      ]);
    },
  });

  const d = preview.data?.data;
  const warnings = costWarnings(p, d);

  return (
    <Modal
      wide
      title={`Өртөг хуваарилах · ${p.code ?? ''}`}
      subtitle="Хуваарилсны дараа зардал, аргыг өөрчлөх боломжгүй"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => allocate.mutate()} disabled={!d || preview.isFetching || allocate.isPending}>
            {allocate.isPending ? 'Хуваарилж байна…' : `${formatQty(d?.lines.length ?? 0)} гутланд хуваарилах`}
          </Button>
        </>
      }
    >
      <Tabs tabs={METHODS.map((m) => ({ key: m, label: ALLOCATION_LABEL[m] }))} value={method} onChange={onMethod} />

      {preview.isPending ? <Spinner /> : null}
      {preview.error ? <ErrorNote>{(preview.error as Error).message}</ErrorNote> : null}

      {d ? (
        <>
          <p className="text-meta text-ink-2">
            Нэмэлт зардал <span className="num text-ink">{formatMnt(d.overheadTotalMnt)}</span> ·{' '}
            {formatQty(d.lines.length)} ш · дугуйруулга {formatMnt(d.roundingAdjustmentMnt)} сүүлийн гутланд
          </p>
          {warnings.map((w) => (
            <p key={w} className="rounded-well bg-warn-bg px-3.5 py-2.5 text-meta text-warn-ink">{w}</p>
          ))}
          <div className="max-h-[50dvh] overflow-y-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Серийн</Th>
                  <Th num>Размер</Th>
                  <Th num>Барааны өртөг</Th>
                  <Th num>Нэмэлт</Th>
                  <Th num>Нийт өртөг</Th>
                </tr>
              </thead>
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.itemId}>
                    <Td><span className="num">{l.serial ? formatSerialDisplay(l.serial) : 'шошгогүй'}</span></Td>
                    <Td num>{l.size ?? '—'}</Td>
                    <Td num>{formatMnt(l.goodsCostMnt)}</Td>
                    <Td num>{formatMnt(l.overheadMnt)}</Td>
                    <Td num>{formatMnt(l.landedCostMnt)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      ) : null}

      {allocate.error ? <ErrorNote>{(allocate.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}
